import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useJobCategories } from '../../context/JobCategoryContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import Breadcrumb from '../common/Breadcrumb';
import {
  TagIcon,
  ArrowLeftIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
  ClockIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

const JobCategoryView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const getRoutePath = useRoutePath();
  const { hideLoading } = useLoading();
  const { jobCategories, getJobCategoryById } = useJobCategories();

  const [category, setCategory] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const hasFetchedCategory = useRef(false);

  const fetchCategory = useCallback(async () => {
    if (!id || hasFetchedCategory.current) return;
    hasFetchedCategory.current = true;
    setIsFetching(true);

    try {
      // Use the API call for full details (includes parent, job_priority relations)
      const response = await getJobCategoryById(id);
      if (response?.status === 200 && response?.data) {
        setCategory(response.data);
      } else {
        setCategory(null);
      }
    } catch (error) {
      console.error('Error fetching job category data:', error);
      setCategory(null);
    } finally {
      setIsFetching(false);
      hideLoading();
    }
  }, [id, getJobCategoryById, hideLoading]);

  useEffect(() => {
    // Quick resolve from context list
    if (id && jobCategories?.data?.length && !category) {
      const existing = jobCategories.data.find(
        (item) => Number(item.id) === Number(id)
      );
      if (existing && !hasFetchedCategory.current) {
        setCategory(existing);
      }
    }
    fetchCategory();
  }, [id, jobCategories, category, fetchCategory]);

  const handleEdit = () => {
    navigate(getRoutePath(`/job-categories/edit/${id}`));
  };

  /* ─── helpers ────────────────────────────────────────── */

  const getStatusBadge = () => {
    const isActive = category?.active === 1 || category?.active === true;
    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}
      >
        {isActive ? (
          <>
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Active
          </>
        ) : (
          <>
            <XCircleIcon className="h-4 w-4 mr-1" />
            Inactive
          </>
        )}
      </span>
    );
  };

  const getBoolBadge = (value, yesLabel = 'Yes', noLabel = 'No', yesColor = 'blue') => {
    const isTrue = value === 1 || value === true;
    const colorMap = {
      blue: isTrue ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600',
      green: isTrue ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600',
    };
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${colorMap[yesColor] || colorMap.blue}`}>
        {isTrue ? (
          <>
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            {yesLabel}
          </>
        ) : (
          <>
            <XCircleIcon className="h-4 w-4 mr-1" />
            {noLabel}
          </>
        )}
      </span>
    );
  };

  const fmt = (v) => {
    if (v === null || v === undefined || v === '') return 'N/A';
    return v;
  };

  /* ─── loading / not-found states ─────────────────────── */

  if (!isFetching && !category) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Job Category Not Found</h2>
          <p className="text-gray-600 mb-6">
            The job category you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <button
            onClick={() => navigate(getRoutePath('/job-categories'))}
            className="inline-flex items-center px-4 py-2 bg-[#009efb] text-white rounded-lg hover:bg-[#0089db] transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Job Categories
          </button>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-3">
          <div className="h-8 w-8 border-2 border-[#009efb] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Loading job category information...</p>
        </div>
      </div>
    );
  }

  /* ─── derived data ───────────────────────────────────── */

  const title = category.name || 'Job Category Details';
  const parentName = category.parent?.name || null;
  const isSubCategory = !!category.parent_category_id;
  const hasAutoPreLoad = category.is_automatically_pre_load === 1 || category.is_automatically_pre_load === true;
  const priorityName = category.job_priority?.name || null;
  const priorityClass = category.job_priority?.class || null;

   
  /* ─── render ─────────────────────────────────────────── */

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* ─── Header ─── */}
        <div className="mb-8">
          <Breadcrumb />
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 space-y-4 md:space-y-0">
            <div className="flex items-start md:items-center space-x-3">
              <div className="p-2 bg-[#009efb] rounded-lg">
                <TagIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center flex-wrap gap-2">
                  <span>{title}</span>
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  <span>{getStatusBadge()}</span>
                  {isSubCategory && parentName && (
                    <span className="text-gray-500">Parent: {parentName}</span>
                  )}
                  {!isSubCategory && (
                    <span className="text-gray-500">Main Category</span>
                  )}
                  {category.formated_created_at && (
                    <span className="text-gray-500">Created: {category.formated_created_at}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:flex-wrap gap-2 w-full md:w-auto">
              <button
                onClick={() => navigate(getRoutePath('/job-categories'))}
                className="inline-flex items-center justify-center px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md bg-white hover:bg-gray-50 transition-colors w-full sm:w-auto"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to List
              </button>
              <button
                onClick={handleEdit}
                className="inline-flex items-center justify-center px-4 py-2 text-sm bg-[#009efb] text-white rounded-md hover:bg-[#0089db] transition-colors w-full sm:w-auto"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Category
              </button>
            </div>
          </div>
        </div>

        {/* ─── Main Grid ─── */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* ─── Left Column ─── */}
          <div className="xl:col-span-2 space-y-6">
            {/* Category Information Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <TagIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">Category Information</h2>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category Name</label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{fmt(category.name)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent Category</label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {parentName || 'None (Main Category)'}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <div className="p-3 bg-gray-50 rounded-lg">{getStatusBadge()}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Auto Pre-Load</label>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      {getBoolBadge(category.is_automatically_pre_load, 'Enabled', 'Disabled')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Auto Pre-Load Defaults Card (conditional) */}
            {hasAutoPreLoad && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                  <div className="flex items-center">
                    <Cog6ToothIcon className="h-5 w-5 text-white mr-3" />
                    <h2 className="text-xl font-semibold text-white">Pre-Load Defaults</h2>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Default Job Priority</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                        {priorityName ? (
                          <span className={priorityClass ? `text-${priorityClass} font-semibold` : 'font-semibold'}>
                            {priorityName}
                          </span>
                        ) : (
                          'N/A'
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Default Duration</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900 flex items-center">
                        <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                        {fmt(category.default_duration)}
                      </div>
                    </div>
                  </div>

                  {category.default_job_description && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Default Job Description</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-900 min-h-[60px] whitespace-pre-wrap">
                        {category.default_job_description}
                      </div>
                    </div>
                  )}

                  {category.default_notes_for_techs && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Default Notes For Techs</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-900 min-h-[60px] whitespace-pre-wrap">
                        {category.default_notes_for_techs}
                      </div>
                    </div>
                  )}

                  {Array.isArray(category?.default_custom_fields) && category?.default_custom_fields.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Default Custom Fields</label>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex flex-wrap gap-2">
                          {category?.default_custom_fields.map((field, index) => {
                            // Try to resolve field name from category.custom_fields relation
                            
                            return (
                              <span
                                key={index}
                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                              >
                                {field?.name || `Field #${index}`}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ─── Right Column ─── */}
          <div className="space-y-6">
            {/* Category Summary Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">Category Summary</h2>
              </div>
              <div className="p-6 space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  {getStatusBadge()}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Type</span>
                  <span className="font-medium text-gray-900">
                    {isSubCategory ? 'Sub Category' : 'Main Category'}
                  </span>
                </div>
                {parentName && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Parent</span>
                    <span className="font-medium text-gray-900">{parentName}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Auto Pre-Load</span>
                  {getBoolBadge(category.is_automatically_pre_load, 'Enabled', 'Disabled')}
                </div>
                {hasAutoPreLoad && priorityName && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Priority</span>
                    <span className={`font-medium ${priorityClass ? `text-${priorityClass}` : 'text-gray-900'}`}>
                      {priorityName}
                    </span>
                  </div>
                )}
                {hasAutoPreLoad && category.default_duration && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Duration</span>
                    <span className="font-medium text-gray-900 flex items-center">
                      <ClockIcon className="h-4 w-4 text-gray-400 mr-1" />
                      {category.default_duration}
                    </span>
                  </div>
                )}
                {category.formated_created_at && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex items-center text-xs font-medium text-gray-500 mb-1">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      Created At
                    </div>
                    <div className="text-sm text-gray-900">{category.formated_created_at}</div>
                  </div>
                )}
                {category.formated_updated_at && (
                  <div>
                    <div className="flex items-center text-xs font-medium text-gray-500 mb-1">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      Last Updated
                    </div>
                    <div className="text-sm text-gray-900">{category.formated_updated_at}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobCategoryView;
